import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Button, Col, Form } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { DEFAULT_COUNTRY_CODE } from '@constants/constants';
import { TextField } from '@components/Form/TextField';
import { getAddress } from '@services/address-client';
import styles from './Address.module.scss';

export const Address = ({
  delivery = false,
  readOnly = false
}: {
  delivery?: boolean;
  readOnly?: boolean | { postalCode?: boolean; houseNumber?: boolean; houseNumberAddition?: boolean; street?: boolean; city?: boolean };
}) => {
  const { t } = useTranslation('register');

  const manualAddressString = delivery ? 'deliveryManualAddress' : 'manualAddress';
  const postalCodeString = delivery ? 'deliveryPostalCode' : 'postalCode';
  const houseNumberString = delivery ? 'deliveryHouseNumber' : 'houseNumber';
  const houseNumberAdditionString = delivery ? 'deliveryHouseNumberAddition' : 'houseNumberAddition';
  const cityString = delivery ? 'deliveryCity' : 'city';
  const streetString = delivery ? 'deliveryStreet' : 'street';

  const formikContext = useFormikContext<any>();
  const manualAddress = formikContext.values[manualAddressString] ?? '';
  const street = formikContext.values[streetString] ?? '';
  const houseNumber = formikContext.values[houseNumberString] ?? '';
  const houseNumberAddition = formikContext.values[houseNumberAdditionString] ?? '';
  const postalCode = formikContext.values[postalCodeString] ?? '';
  const city = formikContext.values[cityString] ?? '';
  const [shouldShowAdressBlock, setShowAddressBlock] = useState(false);
  const [isAdressApiAvailable, setAdressApiAvailable] = useState(true);
  const [isApiAdressValid, setApiAdressValid] = useState<boolean | undefined>(undefined);

  // Handle both boolean and object readOnly prop
  const getFieldReadOnly = (fieldName: string) => {
    if (typeof readOnly === 'boolean') {
      return readOnly && !delivery;
    }
    return readOnly?.[fieldName as keyof typeof readOnly] || false;
  };

  const doAddressLookup = async () => {
    if (!!postalCode && !!houseNumber && !manualAddress) {
      try {
        const response = await getAddress({
          postalCode: postalCode,
          houseNumber: houseNumber,
          houseNumberAddition: houseNumberAddition
        });

        formikContext.setFieldValue(streetString, response?.street ?? '');
        formikContext.setFieldValue(cityString, response?.city ?? '');
        setShowAddressBlock(true);
        setAdressApiAvailable(true);
        setApiAdressValid(true);
      } catch (e) {
        // TODO: error reporting
        // Response did not return 2xx status
        setAdressApiAvailable(false);
        setApiAdressValid(false);
        setShowAddressBlock(false);

        formikContext.setFieldTouched(manualAddressString, true, false);
        formikContext.setFieldValue(manualAddressString, true);

        formikContext.setFieldTouched(streetString, false, false);
        formikContext.setFieldValue(streetString, '');
        formikContext.setFieldTouched(cityString, false, false);
        formikContext.setFieldValue(cityString, '');
      }
    }
  };

  // #13018 in devops. Required by transporter aperently
  useEffect(() => {
    const countryCode = formikContext.values['countryCode'];

    if (countryCode === DEFAULT_COUNTRY_CODE && postalCode.length === 6 && !postalCode.includes(' ')) {
      const prefix = postalCode.slice(0, -2);
      const suffix = postalCode.slice(-2);
      formikContext.setFieldValue('postalCode', `${prefix} ${suffix}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode]);

  useEffect(() => {
    const keys = [postalCodeString, houseNumberString, cityString, streetString];
    const isValid =
      // Has a value ( !! )
      keys.every((current) => !!formikContext.values[current]) &&
      // Not has an error
      keys.every((currentValue) => !formikContext.errors[currentValue]);

    if (isValid) {
      setShowAddressBlock(true);
    } else {
      setShowAddressBlock(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, houseNumber, city, street]);

  const toggleManualAddress = async () => {
    // Transitioning from manual -> automatic, `wasManual` is still `true` till next render
    const wasManual = formikContext.values[manualAddressString];
    const touched = formikContext.touched[streetString] || formikContext.touched[cityString];

    if (touched === true && wasManual === true) {
      await doAddressLookup();
    }
    formikContext.setFieldTouched(manualAddressString, true, false);
    formikContext.setFieldValue(manualAddressString, !formikContext.values[manualAddressString]);
  };

  return (
    <>
      <Form.Row>
        <Form.Group as={Col} xs={12} md={6}>
          <TextField
            label={t('label.postal-code')}
            type="text"
            name={postalCodeString}
            onBlur={doAddressLookup}
            readOnly={getFieldReadOnly('postalCode')}
          />
        </Form.Group>
        <Form.Group as={Col} xs={12} md={4}>
          <TextField
            label={t('label.house-number')}
            type="text"
            name={houseNumberString}
            onBlur={doAddressLookup}
            min={0}
            readOnly={getFieldReadOnly('houseNumber')}
          />
        </Form.Group>
        <Form.Group as={Col} xs={12} md={2}>
          <TextField
            placeholder={t('label.house-number-addition')}
            type="text"
            name={houseNumberAdditionString}
            onBlur={doAddressLookup}
            readOnly={getFieldReadOnly('houseNumberAddition')}
          />
        </Form.Group>
      </Form.Row>

      <Form.Row className={manualAddress || isApiAdressValid === false ? '' : styles.hiddenField}>
        <Form.Group as={Col} xs={12} md={6}>
          <TextField label={t('label.street')} type="text" name={streetString} readOnly={getFieldReadOnly('street')} />
        </Form.Group>
        <Form.Group as={Col} xs={12} md={6}>
          <TextField label={t('label.city')} type="text" name={cityString} readOnly={getFieldReadOnly('city')} />
        </Form.Group>
      </Form.Row>

      {shouldShowAdressBlock && (
        <Form.Group>
          <Form.Row>
            <Form.Group as={Col} xs={12} md={6}>
              <div className={`${styles.addressBlock} p-3`}>
                <span className="font-weight-bold text-white" data-testid="address-block">
                  {t('title.address')}
                </span>
                <br />
                {street ? (
                  <>
                    <span className="text-white street">{street}</span>
                    <span className="text-white houseNumber">
                      {houseNumber}
                      {houseNumberAddition && ` ${houseNumberAddition}`}
                    </span>
                    <br />
                  </>
                ) : (
                  <>
                    <span className={styles.addresLine}></span>
                    <br />
                  </>
                )}

                {postalCode ? (
                  <>
                    <span className="text-white postalCode">{postalCode}</span> <span className="text-white city">{city}</span>
                    <br />
                  </>
                ) : (
                  <>
                    <span className={styles.addresLine}></span>
                    <br />
                  </>
                )}

                <input
                  type="checkbox"
                  name={manualAddressString}
                  checked={manualAddress}
                  className={`${styles.manualAddressSelection} ${styles.hiddenField}`}
                  readOnly
                />
                {isAdressApiAvailable && !manualAddress && (
                  <>
                    <br />
                    <Button
                      type="button"
                      variant="link"
                      onClick={toggleManualAddress}
                      className={`${styles.toggleManualAddress} p-0`}
                      data-testid="toggleManualAddress"
                    >
                      <span className={styles.titleManualAddress}>{t('button.manual-address-selection')}</span>
                    </Button>
                  </>
                )}
              </div>
            </Form.Group>
          </Form.Row>
        </Form.Group>
      )}
    </>
  );
};
